// frontend/src/components/community/CategoryTabs.js
import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Package, TrendingUp, Grid, List } from 'lucide-react';

const CategoryTabs = ({ 
  categories, 
  activeCategory, 
  onCategoryChange,
  itemCounts,
  viewMode,
  onViewModeChange,
  sortBy,
  onSortChange,
  searchQuery,
  onSearchChange
}) => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const categoryIcons = {
    TECHNOLOGY: '💻',
    MARKETING: '📈',
    DESIGN: '🎨',
    TELECOM: '📱',
    CONSULTING: '💼',
    CONTENT: '📝',
    SERVICES: '🛠️',
    PRODUCTS: '📦',
    JOBS: '👥'
  };

  const sortOptions = [
    { value: 'recommended', label: 'Recommended', icon: '⭐' },
    { value: 'price_asc', label: 'Price: Low to High', icon: '💰' },
    { value: 'price_desc', label: 'Price: High to Low', icon: '💸' },
    { value: 'newest', label: 'Newest First', icon: '🆕' },
    { value: 'popular', label: 'Most Popular', icon: '🔥' },
    { value: 'rating', label: 'Highest Rated', icon: '⭐' }
  ];

  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10);
      }
    };
    
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      checkScroll();
      return () => container.removeEventListener('scroll', checkScroll);
    }
  }, [categories]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const getSortLabel = () => {
    const option = sortOptions.find(opt => opt.value === sortBy);
    return option ? option.label : 'Sort';
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Search and View Controls Row */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        {/* Search Input */}
        <div className="flex-1 relative">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search in this shop..."
            className="w-full px-3 sm:px-4 py-1.5 sm:py-2 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs sm:text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>

        {/* Sort Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            className="w-full sm:w-auto px-3 sm:px-4 py-1.5 sm:py-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs sm:text-sm font-medium text-gray-700 flex items-center justify-center gap-1.5"
          >
            <span>{getSortLabel()}</span>
            <svg className={`h-3 w-3 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showSortDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowSortDropdown(false)} />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-neomorph border border-primary-100/30 z-20 overflow-hidden">
                {sortOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onSortChange(option.value);
                      setShowSortDropdown(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs sm:text-sm hover:bg-gray-50 transition-colors ${
                      sortBy === option.value ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                    }`}
                  >
                    <span>{option.icon}</span>
                    <span className="flex-1 text-left">{option.label}</span>
                    {sortBy === option.value && <span className="text-primary-600">✓</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center bg-white rounded-lg shadow-neomorph p-0.5">
          <button
            onClick={() => onViewModeChange('grid')}
            className={`p-1.5 sm:p-2 rounded-lg transition-all ${
              viewMode === 'grid' 
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="Grid view"
          >
            <Grid className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`p-1.5 sm:p-2 rounded-lg transition-all ${
              viewMode === 'list' 
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="List view"
          >
            <List className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </button>
        </div>
      </div>

      {/* Category Tabs with Scroll Arrows */}
      <div className="relative">
        {/* Left Scroll Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-1 shadow-neomorph hover:shadow-neomorph-hover transition-all -ml-2 sm:-ml-3"
          >
            <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
          </button>
        )}

        {/* Tabs Container */}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto hide-scrollbar space-x-1 sm:space-x-1.5 pb-2 px-0.5"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* ALL Tab */}
          <button
            onClick={() => onCategoryChange('ALL')}
            className={`
              flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap
              ${activeCategory === 'ALL' 
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30' 
                : 'bg-white text-gray-700 shadow-neomorph hover:shadow-neomorph-hover'
              }
            `}
          >
            <span>ALL</span>
            <span className={`ml-1.5 sm:ml-2 px-1.5 py-0.5 rounded-full text-[9px] sm:text-xs ${
              activeCategory === 'ALL'
                ? 'bg-white/20 text-white'
                : 'bg-primary-100 text-primary-700'
            }`}>
              {itemCounts.ALL || 0}
            </span>
          </button>

          {/* Category Tabs */}
          {categories.map(category => (
            <button
              key={category.name}
              onClick={() => onCategoryChange(category.name)}
              className={`
                flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap
                ${activeCategory === category.name 
                  ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30' 
                  : 'bg-white text-gray-700 shadow-neomorph hover:shadow-neomorph-hover'
                }
              `}
            >
              <span className="mr-1 sm:mr-1.5 text-sm sm:text-base">{categoryIcons[category.name] || '📦'}</span>
              <span className="hidden xs:inline">{category.displayName}</span>
              <span className="xs:hidden">{category.shortName || category.displayName.slice(0, 6)}</span>
              <span className={`ml-1.5 sm:ml-2 px-1.5 py-0.5 rounded-full text-[9px] sm:text-xs ${
                activeCategory === category.name
                  ? 'bg-white/20 text-white'
                  : 'bg-primary-100 text-primary-700'
              }`}>
                {category.count}
              </span>
            </button>
          ))}
        </div>

        {/* Right Scroll Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-1 shadow-neomorph hover:shadow-neomorph-hover transition-all -mr-2 sm:-mr-3"
          >
            <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
          </button>
        )}
      </div>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default CategoryTabs;